import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {
  IGenericType,
  ISupplierUserProfile,
  IWalletTransaction,
} from 'constants/interfaces';
import {
  apiGetBankAccountTypes,
  apiGetBankTypes,
  apiGetPurchaseOrderSupportingDocuments,
  apiUpdatePurchaseOrderStatus,
  apiWalletWithdraw,
} from 'constants/Api';
import {Alert, Skeleton} from '@material-ui/lab';
import moment from 'moment';
import {
  _NumberWithSpaces,
  IPurchaseOrderStatus,
  EPurchaseOrderSupportingDocs,
} from 'constants/index';

export const WalletWithdrawDialog = (props: {
  user: ISupplierUserProfile;
  transaction?: IWalletTransaction;
  open: boolean;
  onConfirm: () => any;
  onCancel: () => any;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasDeliveryNote, setHasDeliveryNote] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);

  const [isLoadingBankInfo, setIsLoadingBankInfo] = useState(true);
  const [bankTypes, setBankTypes] = useState<IGenericType[]>([]);
  const [bankAccountTypes, setBankAccountTypes] = useState<IGenericType[]>([]);

  useEffect(() => {
    setIsLoadingBankInfo(true);
    apiGetBankTypes()
      .then(setBankTypes)
      .then(apiGetBankAccountTypes)
      .then(setBankAccountTypes)
      .catch((error) => {
        alert('Failed to load bank information. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingBankInfo(false));
  }, [props.user]);

  useEffect(() => {
    setHasInvoice(false);
    setHasDeliveryNote(false);
    const purchaseOrder = props.transaction?.inputsPurchaseOrder;
    if (!purchaseOrder) return;
    apiGetPurchaseOrderSupportingDocuments(purchaseOrder.id).then(
      (documents) => {
        const hasDeliveryNote = documents.some(
          (document) =>
            document.title === EPurchaseOrderSupportingDocs.DeliveryNote,
        );
        setHasDeliveryNote(hasDeliveryNote);
        const hasInvoice = documents.some(
          (document) => document.title === EPurchaseOrderSupportingDocs.Invoice,
        );
        setHasInvoice(hasInvoice);
      },
    );
  }, [props.transaction]);

  const handleSubmit = (submission: WalletWithdrawFormSubmission) => {
    setIsLoading(true);
    const purchaseOrder = props.transaction?.inputsPurchaseOrder;
    if (!purchaseOrder) return;
    apiWalletWithdraw(submission.pin, purchaseOrder.id)
      .then(async (response) => {
        if (!response.success) {
          alert(response.message);
          return;
        }
        await apiUpdatePurchaseOrderStatus(
          purchaseOrder.id,
          IPurchaseOrderStatus.Paid,
        );
        props.onConfirm();
      })
      .catch((err) => {
        console.log('Failed to withdraw from wallet', err);
        alert('Unable to withdraw at the moment. Please try again later');
      })
      .finally(() => setIsLoading(false));
  };

  const company = props.user.company;
  const bankName = bankTypes.find((type) => type.id === company?.bankId)?.name;
  const bankAccountType = bankAccountTypes.find(
    (type) => type.id === company?.bankAccountTypeId,
  )?.name;
  const bankAccountNumber = company?.bankAccountNumber;
  const bankBranchCode = company?.bankBranchCode;

  const transaction = props.transaction;

  const expectedPaymentDate =
    transaction?.inputsPurchaseOrder?.expectedPaymentDate;
  const hasReceivedPayment = !expectedPaymentDate
    ? true
    : moment(parseInt(expectedPaymentDate))
        .add(1, 'day')
        .startOf('day')
        .isBefore(moment());

  return (
    <Dialog
      open={props.open}
      onClose={props.onCancel}
      className="update-password-dialog"
    >
      <DialogTitle>Withdraw From Wallet</DialogTitle>
      <DialogContent>
        {!isLoading && !hasDeliveryNote && (
          <Box marginBottom={1}>
            <Alert severity="warning">
              Cannot withdraw without delivery note
            </Alert>
          </Box>
        )}
        {!isLoading && !hasInvoice && (
          <Box marginBottom={1}>
            <Alert severity="warning">Cannot withdraw without invoice</Alert>
          </Box>
        )}
        {!isLoading &&
          hasDeliveryNote &&
          hasInvoice &&
          !hasReceivedPayment &&
          expectedPaymentDate && (
            <Box marginBottom={1}>
              <Alert severity="warning">
                Cannot withdraw before payment date:{' '}
                {moment(parseInt(expectedPaymentDate)).format('YYYY-MM-DD')}
              </Alert>
            </Box>
          )}
        {transaction && hasDeliveryNote && hasInvoice && hasReceivedPayment && (
          <Typography variant="body2">
            Transaction Date:{' '}
            {moment(transaction.TransactionDate).format('YYYY-MM-DD')}
            <br />
            Reference: {transaction.Reference}
            <br />
            Amount: R {_NumberWithSpaces(transaction.Amount)}
            <br />
          </Typography>
        )}
        {isLoadingBankInfo &&
          hasDeliveryNote &&
          hasInvoice &&
          hasReceivedPayment && <Skeleton variant="rect" height={80} />}
        {!isLoadingBankInfo &&
          hasDeliveryNote &&
          hasInvoice &&
          hasReceivedPayment && (
            <Typography variant="body2">
              <br />
              Bank: {bankName}
              <br />
              Account Type: {bankAccountType}
              <br />
              Account Number: {bankAccountNumber}
              <br />
              Branch Code: {bankBranchCode}
              <br />
            </Typography>
          )}
        {hasDeliveryNote && hasInvoice && hasReceivedPayment && (
          <WalletWithdrawForm
            isLoading={isLoading || isLoadingBankInfo}
            submitButtonTitle={'Withdraw'}
            onSubmit={handleSubmit}
            cancelButtonTitle={'Cancel'}
            onCancel={props.onCancel}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export type WalletWithdrawFormSubmission = {
  pin: string;
};

export const WalletWithdrawForm = (props: {
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (submission: WalletWithdrawFormSubmission) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
}) => {
  type Submission = WalletWithdrawFormSubmission;

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control} = useForm<Submission>();

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      pin: submission.pin,
    });
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Controller
                name="pin"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Pin"
                    variant="outlined"
                    type="password"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.isLoading}
                  />
                )}
                rules={{
                  required: 'Pin required',
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            style={{paddingTop: '1rem', paddingBottom: '1rem'}}
          >
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className={'secondary-btn'}
                  variant="contained"
                  disableElevation
                  fullWidth
                  disabled={props.isLoading}
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className={'primary-btn'}
                type="submit"
                variant="contained"
                disableElevation
                fullWidth
                color="primary"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress color={'primary'} size={24} thickness={6} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
};
